import { signInWithEmailAndPassword } from "firebase/auth";
import {
    axiosAuth,
    axiosPlatform,
    prepareAxiosAuth,
    prepareAxiosPlatform,
    supportAuthorization,
} from "../../_services/axios-helper";
import { auth } from "../../firebase";
import { refreshBasicDataRequest } from "../../commons/services/commons.service";

const getCurrentUser = () => {
    return axiosAuth().get("/platform/user");
};

const login = async (email, password, selectedOrganization) => {
    auth.tenantId = process.env.REACT_APP_AUTH_TENANT_ID;
    
    const firebaseUser = await signInWithEmailAndPassword(auth, email, password);
    const accessToken = await firebaseUser.user.getIdToken();
    let tokenUser = {
        access_token: accessToken,
    }
    await mergeUserIntoStorage(tokenUser);

    if(selectedOrganization){

        await selectOrganization(selectedOrganization)
        const currentUser = auth.currentUser;
        if (currentUser) {
            try {
                // Force refresh the token to get updated claims from Firebase
                const refreshedToken = await currentUser.getIdToken(true);
                    // Get the expiration time from the token claims
                const tokenResult = await currentUser.getIdTokenResult();
                const expirationTime = tokenResult.expirationTime;

                let tokenUser = { 
                    access_token: refreshedToken, 
                    expiration_time: expirationTime,
                };

                await mergeUserIntoStorage(tokenUser);
            } catch (tokenRefreshError) {
                console.error('Failed to refresh Firebase token:', tokenRefreshError);
                // Continue execution even if token refresh fails
            }
        }
    }

    return processOAuthToken(tokenUser)
};


const getCelloToken = async () => {
    const data = await axiosAuth().get("/cello/token");
    let existingUser = JSON.parse(localStorage.getItem("user"));
    if (existingUser) {
        mergeUserIntoStorage({ celloToken: data });
    }
};

const logout = () => {
    // Remove Token on Server
    return supportAuthorization(prepareAxiosAuth()).post("/platform/user/logout");
};

const register = (user) => {
    return axiosAuth().post("/register", user);
};

const deleteUser = (externalUUID) => {
    return axiosAuth().delete("/platform/user/" + externalUUID);
};

const registerWithAuthData = (requestBody) => {
    return axiosPlatform().post("/setup/register", requestBody);
};

const autologinShopify = (requestBody) => {
    return axiosPlatform().post("/oauth/shopify/autologin", requestBody);
};

const autologinLexOffice = (requestBody) => {
    return axiosPlatform().post("/oauth/lexoffice/autologin", requestBody);
};

const autologinStripe = (requestBody) => {
    return axiosPlatform().post("/oauth/stripe-marketplace/autologin", requestBody);
};

const installStripe = (requestBody) => {
    return axiosPlatform().post("/stripe-marketplace/setup", requestBody);
};

const createStripeOrgForExistingUser = (requestBody) => {
    return axiosPlatform().post("/stripe-marketplace/connect-new-org", requestBody);
};

const createNewStripeShopForExistingOrgAndUser = (requestBody) => {
    return axiosPlatform().post("/stripe-marketplace/create-shop", requestBody);
};

const resendStripeEmailConfirmation = (nounce) => {
    return axiosPlatform().get("/stripe-marketplace/resend-email/" + nounce);
};

const useOneTimeShopifyToken = (oneTimeToken) => {
    return prepareAxiosPlatform()
        .get("/oauth/shopify/login/" + oneTimeToken)
        .then(
            (response) => {
                return processOAuthToken(response.data);
            },
            (error) => {
                if (error.response) {
                    return Promise.reject("Error: " + error.response.statusText);
                } else if (error.request) {
                    return Promise.reject(error.request);
                } else {
                    return Promise.reject(error.message);
                }
            }
        );
};

const useOneTimeLexOfficeToken = (oneTimeToken) => {
    return supportAuthorization(prepareAxiosPlatform())
        .get("/oauth/lexoffice/login/" + oneTimeToken)
        .then(
            (response) => {
                return processOAuthToken(response.data);
            },
            (error) => {
                if (error.response) {
                    return Promise.reject("Error: " + error.response.statusText);
                } else if (error.request) {
                    return Promise.reject(error.request);
                } else {
                    return Promise.reject(error.message);
                }
            }
        );
};

const useOneTimeStripeToken = (oneTimeToken) => {
    return supportAuthorization(prepareAxiosPlatform())
        .get("/oauth/stripe-marketplace/login/" + oneTimeToken)
        .then(
            (response) => {
                return processOAuthToken(response.data);
            },
            (error) => {
                if (error.response) {
                    return Promise.reject("Error: " + error.response.statusText);
                } else if (error.request) {
                    return Promise.reject(error.request);
                } else {
                    return Promise.reject(error.message);
                }
            }
        );
};

const useRefreshToken = async () => {
    try {
        // Import Firebase auth and wait for initialization
        const { auth } = await import("../../firebase");

        // Wait for Firebase to initialize auth state with timeout
        const currentUser = await new Promise((resolve, reject) => {
            // If user is already available, resolve immediately
            if (auth.currentUser) {
                resolve(auth.currentUser);
                return;
            }

            const timeout = setTimeout(() => {
                unsubscribe();
                reject(new Error("Firebase auth initialization timeout"));
            }, 5000); // 5 second timeout

            const unsubscribe = auth.onAuthStateChanged((user) => {
                clearTimeout(timeout);
                unsubscribe();
                resolve(user);
            });
        });

        if (!currentUser) {
            throw new Error("No authenticated user found");
        }

        // Force refresh the Firebase token
        const refreshedToken = await currentUser.getIdToken(true);

        // Get the updated expiration time
        const tokenResult = await currentUser.getIdTokenResult();
        const expirationTime = tokenResult.expirationTime;

        // Update the stored user data with the new token
        const tokenUser = {
            access_token: refreshedToken,
            expiration_time: expirationTime,
        };
        
        await mergeUserIntoStorage(tokenUser);
        const selectedOrganization = JSON.parse(localStorage.getItem("user"))?.selectedOrganization?.externalUUID || JSON.parse(localStorage.getItem("user"))?.selectedOrganization || "";
        if(selectedOrganization){
            await selectOrganization(selectedOrganization)
            const currentUser = auth.currentUser;
            if (currentUser) {
                try {
                    // Force refresh the token to get updated claims from Firebase
                    const refreshedToken = await currentUser.getIdToken(true);
                        // Get the expiration time from the token claims
                    const tokenResult = await currentUser.getIdTokenResult();
                    const expirationTime = tokenResult.expirationTime;

                    let tokenUser = { 
                        access_token: refreshedToken, 
                        expiration_time: expirationTime,
                    };

                    await mergeUserIntoStorage(tokenUser);
                    await refreshBasicDataRequest();
                
                } catch (tokenRefreshError) {
                    console.error('Failed to refresh Firebase token:', tokenRefreshError);
                    // Continue execution even if token refresh fails
                }
            }
        }
        return mergeUserIntoStorage(tokenUser);
    } catch (error) {
        console.error('Firebase token refresh failed:', error);

        // Import history for redirect
        import("../../_services/history").then(({ history }) => {
            // Clear localStorage and redirect to login
            localStorage.removeItem("user");

            // Preserve current URL for redirect after login
            const currentPath = window.location.pathname;
            if (currentPath !== "/login") {
                history.push("/login?redirect_url=" + currentPath);
            } else {
                history.push("/login");
            }
        }).catch(() => {
            // Fallback if history import fails
            localStorage.removeItem("user");
            window.location.href = "/login";
        });

        return Promise.reject(error);
    }
};

const activateAccount = (externalUUID, newPassword, newPasswordMatch) => {
    return axiosAuth().post("/activate", { externalUUID, newPassword, newPasswordMatch });
};

const selectOrganization = (selectedOrganization, emailVerificationCode) => {
    return axiosAuth().post(
        "/platform/user/selectOrganization/" + selectedOrganization
    );
};

const updatePassword = (user) => {
    return axiosAuth().post("/platform/user/update/password", user);
};


const toggleMfa = (mfaEnabled) => {
    return axiosAuth().post("/platform/user/mfa/toggle", {
        mfaEnabled: mfaEnabled
    });
};

const switchProvider = (provider) => {
    return axiosAuth().post("/platform/user/switch-provider?provider=" + encodeURIComponent(provider));
};

const updateUserName = (user) => {
    return axiosAuth().post("/platform/user/update/mail", user);
};

const updateUserProfileInfo = (user) => {
    return axiosAuth().post("/platform/user/update-profile-info", user);
};

const updateUser = (registrationRequest) => {
    return axiosAuth().post("/platform/user/update", registrationRequest);
};

const reloadUserInStorage = () => {
    return getCurrentUser().then((user) => {
        return mergeUserIntoStorage(user);
    });
};

const mergeUserIntoStorage = (user) => {
    if (user) {
        let existingUser = JSON.parse(localStorage.getItem("user"));
        if (existingUser) {
            // Preserve access_token if it exists in storage but not in new data
            const existingToken = existingUser.access_token;
            const existingSelectedOrganization = existingUser.selectedOrganization;

            for (let key in user) {
                existingUser[key] = user[key];
            }
            // Restore token if it was removed
            if (existingToken && !user.access_token) {
                existingUser.access_token = existingToken;
            }
            //  Restore selectedOrganization if it was removed
            if (existingSelectedOrganization && !user.selectedOrganization) {
                existingUser.selectedOrganization = existingSelectedOrganization;
            }
        } else {
            existingUser = user;
        }
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        localStorage.setItem("user", JSON.stringify(existingUser));
        return existingUser;
    }
};

const resetPassword = (externalUUID, newPassword, newPasswordMatch) => {
    return axiosAuth().post("/platform/user/password/reset/" + externalUUID, { newPassword, newPasswordMatch });
};

const setUpResetPassword = (mail) => {
    return axiosAuth().post("/platform/user/password/reset", { mail });
};

const sendActivationLink = (mail) => {
    return axiosAuth().post("/platform/user/sendactivation", { mail });
};

const acceptDocuments = (acceptTos, acceptDpa) => {
    return axiosAuth().post("/platform/user/accept", { acceptTos: acceptTos, acceptDpa: acceptDpa });
};

const processOAuthToken = (tokenData) => {
    // login successful if there's a jwt token in the response
    if (tokenData && tokenData.access_token) {
        // set token into storage, so it can be used on subsequent requests
        mergeUserIntoStorage(tokenData);

        return reloadUserInStorage();
    } else {
        throw new Error("Missing token data");
    }
};
 
// const requestOAuthLogin = (details) => {
//     const params = new URLSearchParams();
//     for (let property in details) {
//         params.append(property, details[property]);
//     }

//     let axiosInstance = prepareAxiosAuth();
//     axiosInstance.defaults.headers.common["Authorization"] = "Basic " + btoa(details.username + ":" + details.password);
//     axiosInstance.defaults.headers.common["Content-Type"] = "application/x-www-form-urlencoded";
//     return supportAuthErrorCodes(axiosInstance)
//         .post("/oauth2/token", params)
//         .then((response) => {
//             return processOAuthToken(response.data);
//         });
// };

const getHubspotSession = () => {
    return axiosAuth().get("/oauth2/callback/hubspot/checksession");
};

const updateLastLogin = () => {
    return axiosAuth().patch("platform/user/last-login");
};

const clearHubspotSession = () => {
    return axiosAuth().get("/oauth2/callback/hubspot/clearsession");
};

const getHubspotVisitorToken = () => {
    return axiosAuth().get("/platform/user/hubspot/get-hubspot-token");
};

const registerNewAppstoreUser = (requestBody) => {
    return axiosPlatform().post("/setup/new-user", requestBody);
};

const createNewAppstoreOrgForExistingUser = (requestBody) => {
    return axiosPlatform().post("/setup/connect-new-org", requestBody);
};

const createNewShopForExistingOrgAndUser = (requestBody) => {
    return axiosPlatform().post("/setup/create-shop", requestBody);
};

const sendCelloRegistrationData = (requestBody) => {
    return axiosAuth().post("/cello/register", requestBody);
};

const updateShopifyNounceUser = (shop, requestBody) => {
    return axiosPlatform().put("/shopify-appstore/nounce/" + shop, requestBody);
};

const getFinToken = () => {
    return axiosAuth().post("/api/fin/chat/token");
};

export const userService = {
    login,
    logout,
    register,
    deleteUser,
    registerWithAuthData,
    autologinShopify,
    autologinLexOffice,
    useOneTimeShopifyToken,
    useOneTimeLexOfficeToken,
    useRefreshToken,
    activateAccount,
    selectOrganization,
    updatePassword,
    updateUserName,
    getCurrentUser,
    reloadUserInStorage,
    mergeUserIntoStorage,
    setUpResetPassword,
    resetPassword,
    sendActivationLink,
    acceptDocuments,
    getHubspotSession,
    clearHubspotSession,
    getHubspotVisitorToken,
    registerNewAppstoreUser,
    createNewAppstoreOrgForExistingUser,
    createNewShopForExistingOrgAndUser,
    useOneTimeStripeToken,
    autologinStripe,
    updateUser,
    getCelloToken,
    sendCelloRegistrationData,
    updateShopifyNounceUser,
    updateLastLogin,
    updateUserProfileInfo,
    installStripe,
    createStripeOrgForExistingUser,
    createNewStripeShopForExistingOrgAndUser,
    resendStripeEmailConfirmation,
    getFinToken,
    processOAuthToken,
    toggleMfa,
    switchProvider
};
